import { ErrorComponent } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { usePlaidLink } from 'react-plaid-link';

import { BASE_PATH } from '@/constants/config';
import { usePlaidVerificationLinkToken } from '@/hooks/api/use-plaid-verification-link-token';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import {
  IDENTITY_JOURNEY_MAX_AGE_SEC,
  setLinkedinVolIdentityProfileIdCookie,
  setLinkedinVolReturnPathCookie,
} from '@/utils/linkedin-vol-return-cookie';

import { PersonaVerificationContent } from './persona-verification';
import { ProjectDetailSkeleton } from './project-detail-skeleton';

export function PlaidVerification({
  userId,
  projectId,
  profileId,
  onSuccess,
  onCancel,
}: {
  userId: string;
  projectId: string;
  profileId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const { data, isPending, isError } = usePlaidVerificationLinkToken(
    userId,
    projectId,
  );

  if (isPending) {
    return <ProjectDetailSkeleton />;
  }

  // The server routes to the LinkedIn identity journey instead of Plaid when
  // the member can verify (or already verified) their government ID there.
  if (!isError && data?.linkedin_identity_verification && profileId) {
    return (
      <LinkedinIdentityRedirect
        profileId={profileId}
        projectId={projectId}
        userId={userId}
      />
    );
  }

  // ...or to Persona, when the server created an inquiry there instead of a
  // Plaid link token.
  if (!isError && data?.persona_inquiry) {
    return (
      <PersonaVerificationContent
        inquiryId={data.persona_inquiry.inquiry_id}
        sessionToken={data.persona_inquiry.session_token}
        environmentId={data.persona_inquiry.environment_id}
        projectId={projectId}
        userId={userId}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    );
  }

  if (isError || !data?.link_token) {
    return (
      <ErrorComponent error={new Error('Failed to get ID verification link')} />
    );
  }

  return (
    <PlaidVerificationContent
      plaidToken={data.link_token}
      onSuccess={onSuccess}
      onCancel={onCancel}
    />
  );
}

/**
 * Full-page navigation into the LinkedIn OAuth flow with `intent=identity`:
 * consent was already granted, so the hop is invisible and lands on a freshly
 * fetched LinkedIn verificationUrl (never stored). The cookies let the server
 * return the participant to this exact page afterwards.
 */
function LinkedinIdentityRedirect({
  profileId,
  projectId,
  userId,
}: {
  profileId: string;
  projectId: string;
  userId: string;
}) {
  const analytics = useAnalytics();
  // Strict-Mode double-mount guard: navigate (and track) only once.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    analytics?.track(
      {
        action: 'started',
        actionSubject: 'linkedinIdVerification',
        attributes: { projectId, userId },
      },
      // location.assign() unloads the page before the default batched flush;
      // immediate:true routes through sendBeacon.
      { immediate: true },
    );

    setLinkedinVolReturnPathCookie(
      globalThis.location.pathname + globalThis.location.search,
      IDENTITY_JOURNEY_MAX_AGE_SEC,
    );
    setLinkedinVolIdentityProfileIdCookie(profileId);

    globalThis.location.assign(
      `${BASE_PATH}/v4/profiles/${profileId}/linkedin-vol/authorize?intent=identity`,
    );
  }, [analytics, profileId, projectId, userId]);

  return <ProjectDetailSkeleton />;
}

function PlaidVerificationContent({
  plaidToken,
  onSuccess,
  onCancel,
}: {
  plaidToken: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const { open, ready } = usePlaidLink({
    token: plaidToken,
    onSuccess,
    onExit: onCancel,
  });

  useEffect(() => {
    if (ready) {
      open();
    }
  }, [ready, open, plaidToken]);

  return null;
}
